import { useCallback, useEffect, useRef } from 'react'

declare global {
    interface Window {
        grecaptcha?: {
            ready: (cb: () => void) => void
            execute: (
                siteKey: string,
                opts: { action: string }
            ) => Promise<string>
        }
    }
}

const SCRIPT_ID = 'casa-recaptcha-v3'

/**
 * reCAPTCHA v3, done with a script tag instead of a dependency.
 *
 * `scripts/build.sh` wipes node_modules and reinstalls on every build, so each new
 * package is a recurring peer-resolution risk. v3 needs one script and one call,
 * which isn't worth that.
 *
 * An empty `siteKey` disables the whole thing: the script is never injected and
 * `execute()` resolves to ''. n8n mirrors this — no secret configured means no
 * check — so the form works end-to-end before the keys exist.
 */
const useRecaptcha = (siteKey?: string) => {
    const enabled = Boolean(siteKey)
    const loadingRef = useRef<Promise<void> | null>(null)

    useEffect(() => {
        if (!enabled) return
        if (document.getElementById(SCRIPT_ID)) return

        const script = document.createElement('script')
        script.id = SCRIPT_ID
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
        script.async = true
        document.head.appendChild(script)
    }, [enabled, siteKey])

    const waitForGrecaptcha = useCallback(() => {
        if (loadingRef.current) return loadingRef.current

        const pending = new Promise<void>((resolve, reject) => {
            const started = Date.now()
            const tick = () => {
                if (window.grecaptcha?.execute) {
                    window.grecaptcha.ready(() => resolve())
                    return
                }
                if (Date.now() - started > 10_000) {
                    reject(new Error('reCAPTCHA failed to load'))
                    return
                }
                window.setTimeout(tick, 100)
            }
            tick()
        })

        // Drop the cached promise if it rejects. Without this a single slow load would
        // be memoised as a permanent failure: every later submit would reuse the settled
        // rejection and fail instantly, so the vendor could never submit without
        // reloading the page — even once the script had finished loading.
        pending.catch(() => {
            if (loadingRef.current === pending) loadingRef.current = null
        })

        loadingRef.current = pending
        return pending
    }, [])

    const execute = useCallback(
        async (action: string): Promise<string> => {
            if (!enabled || !siteKey) return ''
            await waitForGrecaptcha()
            return window.grecaptcha!.execute(siteKey, { action })
        },
        [enabled, siteKey, waitForGrecaptcha]
    )

    return { enabled, execute }
}

export default useRecaptcha
